import { type FC } from 'react';
import cx from 'classnames';

import styles from './PaginationDots.module.css';

type Props = {
  count: number;
  activeIndex: number;
  onSelect: (index: number) => void;
  /** Stays interactive but visually muted while slide transition is in
   *  flight. Prevents rapid-clicks queueing during the 450 ms crossfade. */
  busy?: boolean;
};

/**
 * Pagination dots with distance-from-active dynamic sizing:
 *   - distance 0 (active): pill 20×8, opaque `--color-poi-pagination-active`
 *   - distance 1: circle 8×8, 40% opacity
 *   - distance 2: circle 6×6, 20% opacity
 *   - distance ≥ 3: circle 4×4, 10% opacity
 *
 * Dots are buttons — click switches to that slide (caller drives the
 * camera fly-to in its `onSelect`). All size / colour transitions animate
 * smoothly (200 ms) so the active dot "morphs" rather than snaps.
 */
export const PaginationDots: FC<Props> = ({
  count,
  activeIndex,
  onSelect,
  busy,
}) => {
  if (count <= 1) return null;
  return (
    <div className={cx(styles.dots, busy && styles.busy)} role="tablist">
      {Array.from({ length: count }).map((_, i) => {
        const distance = Math.abs(i - activeIndex);
        const sizeClass =
          distance === 0
            ? styles.active
            : distance === 1
            ? styles.near
            : distance === 2
            ? styles.far
            : styles.farther;
        return (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={distance === 0}
            aria-label={`Slide ${i + 1} of ${count}`}
            className={cx(styles.dot, sizeClass)}
            // preventDefault on pointerdown so focus / scroll doesn't
            // steal the gesture from the swipe handler on the curtain.
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => onSelect(i)}
          />
        );
      })}
    </div>
  );
};

PaginationDots.displayName = 'PaginationDots';
